import DrawerHeader from '@components/common/DrawerHeader';
import LoopIcon from '@mui/icons-material/Loop';
import React, {useEffect} from 'react';
import {Button} from '@mui/material';
import {showModal} from '@actions/ui';
import {useDispatch} from 'react-redux';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {useTranslation} from 'react-i18next';

import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import EmptyMessage from '@components/common/EmptyMessage';
import ErrorMessage from '@components/common/ErrorMessage';
import LoadingMessage from '@app/components/common/LoadingMessage';
import UploadsList from './UploadsList.jsx';
import useOrgParams from '@app/hooks/useOrgParams';
import {MODALS} from '@app/constants';
import {isUsersOrg} from '@app/util/user';
import {notify} from '@actions/notifications.js';
import {useAuth} from '@apps-common/auth-react';
import {useListUploads} from '@api/asyncIngest.js';

const UploadsPanel = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {asyncFeatureUploads: useAsyncUploads} = useFlags();

  const {user} = useAuth();
  const {orgId} = useOrgParams();
  const isUserOrg = isUsersOrg(user?.claims, orgId);

  const {
    data: uploads,
    error,
    isPending,
  } = useListUploads({enabled: useAsyncUploads});

  useEffect(() => {
    if (error) {
      dispatch(notify({message: error, severity: 'error'}));
    }
  }, [error, dispatch]);

  const uploadButton = () => {
    return (
      <Button
        key="uploadFileButton"
        variant="contained"
        color="primary"
        size="small"
        disabled={!isUserOrg}
        onClick={() => dispatch(showModal(MODALS.UPLOAD))}
        startIcon={<ArrowCircleUpIcon fontSize="small" />}
        sx={{marginLeft: 1.25}}
        data-testid="file-uploads-upload-button"
      >
        {t('uploadNavButton.upload')}
      </Button>
    );
  };

  const renderContent = () => {
    if (isPending) {
      return <LoadingMessage suffix="uploads" />;
    }

    if (error) {
      return <ErrorMessage message="Error loading uploaded files" />;
    }

    if (uploads.length === 0) {
      return (
        <EmptyMessage
          icon={<LoopIcon />}
          title="No uploads found"
          actions={isUserOrg ? [uploadButton()] : []}
        />
      );
    }

    return <UploadsList uploads={uploads} />;
  };

  return (
    <>
      <DrawerHeader
        title="File Uploads"
        action={isUserOrg ? uploadButton() : null}
      />
      {renderContent()}
    </>
  );
};

export default UploadsPanel;
